import React, { useEffect, useRef, useState } from 'react';
import { LuDownload, LuFileText, LuLoaderPinwheel, LuPaperclip, LuTrash2 } from 'react-icons/lu';

import { ATTACHMENT_ACCEPT, MAX_ATTACHMENT_SIZE, StandaloneAttachment } from '../../entities/standalone-attachment';
import {
  deleteStandaloneAttachment,
  listStandaloneAttachments,
  standaloneErrorMessage,
  uploadStandaloneAttachment,
} from '../../shared/api/standalone';
import { AgentAuthorBadge } from '../ui/AgentAuthorBadge';

const formatSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

interface AttachmentsPanelProps {
  taskId: string;
}

// No WS live-update for attachments yet (docs/fe-contracts/standalone-contract.md, MVPB-63) —
// every mutation updates local state directly rather than waiting on a socket event.
export const AttachmentsPanel: React.FC<AttachmentsPanelProps> = ({ taskId }) => {
  const [attachments, setAttachments] = useState<StandaloneAttachment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    listStandaloneAttachments(taskId)
      .then((a) => !cancelled && setAttachments(a))
      .catch((err) => !cancelled && setError(standaloneErrorMessage(err) ?? 'Failed to load attachments.'))
      .finally(() => !cancelled && setIsLoading(false));
    return () => {
      cancelled = true;
    };
  }, [taskId]);

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file name after a delete
    if (!file) return;
    if (file.size > MAX_ATTACHMENT_SIZE) {
      setError('File exceeds the 25MB limit.');
      return;
    }
    setIsUploading(true);
    setError(null);
    try {
      const created = await uploadStandaloneAttachment(taskId, file);
      setAttachments((prev) => [...prev, created]);
    } catch (err) {
      setError(standaloneErrorMessage(err) ?? 'Failed to upload file.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async (attachment: StandaloneAttachment) => {
    if (!window.confirm(`Delete "${attachment.fileName}"?`)) return;
    setDeletingId(attachment._id);
    setError(null);
    try {
      await deleteStandaloneAttachment(taskId, attachment._id);
      setAttachments((prev) => prev.filter((a) => a._id !== attachment._id));
    } catch (err) {
      setError(standaloneErrorMessage(err) ?? 'Failed to delete attachment.');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="space-y-2">
      {error && (
        <div className="alert alert-error py-2">
          <span className="text-sm">{error}</span>
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-2">
          <LuLoaderPinwheel className="animate-spin opacity-40" size={16} />
        </div>
      ) : (
        attachments.length > 0 && (
          <div className="space-y-1.5 max-h-48 overflow-y-auto">
            {attachments.map((a) => (
              <div key={a._id} className="flex items-center gap-2 rounded-lg bg-base-200 px-3 py-2 text-sm">
                {a.mimeType.startsWith('image/') ? (
                  <img src={a.url} alt={a.fileName} className="w-8 h-8 rounded object-cover shrink-0" />
                ) : (
                  <div className="w-8 h-8 rounded bg-base-300 flex items-center justify-center shrink-0">
                    <LuFileText size={14} className="opacity-60" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium text-xs">{a.fileName}</div>
                  <div className="flex items-center gap-1.5 text-xs opacity-50">
                    <span>{formatSize(a.size)}</span>
                    {a.authorSource === 'mcp_agent' && <AgentAuthorBadge clientName={a.clientName} />}
                  </div>
                </div>
                <a
                  href={a.url}
                  download={a.fileName}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-ghost btn-xs btn-circle"
                  title="Download"
                >
                  <LuDownload size={13} />
                </a>
                <button
                  className="btn btn-ghost btn-xs btn-circle"
                  onClick={() => handleDelete(a)}
                  disabled={deletingId === a._id}
                  title="Delete"
                >
                  {deletingId === a._id ? (
                    <span className="loading loading-spinner loading-xs" />
                  ) : (
                    <LuTrash2 size={13} />
                  )}
                </button>
              </div>
            ))}
          </div>
        )
      )}

      {!isLoading && attachments.length === 0 && <p className="text-sm opacity-40 py-1">No attachments yet.</p>}

      <input
        ref={fileInputRef}
        type="file"
        accept={ATTACHMENT_ACCEPT}
        onChange={handleFileSelected}
        className="hidden"
      />
      <button
        className="btn btn-outline btn-sm gap-1.5"
        onClick={() => fileInputRef.current?.click()}
        disabled={isUploading}
      >
        {isUploading ? <LuLoaderPinwheel className="animate-spin" size={14} /> : <LuPaperclip size={14} />}
        {isUploading ? 'Uploading…' : 'Attach file'}
      </button>
    </div>
  );
};
